import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInterview } from '@features/interview/context/InterviewContext';
import { useRecorder } from '@features/media/hooks/useRecorder';
import { useCombinedStream } from '@features/media/hooks/useCombinedStream';
import { useNetwork } from '@features/network/hooks/useNetwork';
import { useLiveKit } from '@features/interview/hooks/useLiveKit';
import { ROUTES, INTERVIEW_PHASES, INTERVIEW_CONFIG, STORAGE_KEYS } from '@shared/utils/constants';
import { formatTime } from '@shared/utils/helpers';
import { Button, Toast, Modal, Spinner } from '@shared/components/ui/UI';
import { VideoPreview } from '@features/media/components/VideoPreview';
import interviewService from '@features/interview/services/interview.service';
import VideoUploadManager from '@features/media/services/videoUpload.service';
import tokenStorage from '@features/auth/utils/tokenStorage';
import {
  CameraIcon,
  CameraOffIcon,
  MicrophoneIcon,
  MicrophoneOffIcon,
  EndCallIcon,
  SettingsIcon,
  TranscriptIcon,
  NotesIcon,
  WifiIcon,
  WifiOffIcon,
  VolumeIcon,
  VolumeOffIcon,
} from '@shared/components/icons';

// AI Interviewer Video Component
const AIInterviewerVideo = ({ remoteVideoTrack = null }) => {
  const videoRef = useRef(null);

  // Attach remote video track if available
  useEffect(() => {
    if (remoteVideoTrack && videoRef.current) {
      remoteVideoTrack.attach(videoRef.current);
      return () => {
        remoteVideoTrack.detach();
      };
    }
  }, [remoteVideoTrack]);

  return (
    <div className="relative w-full h-full bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950 overflow-hidden">
      {/* Remote video (AI interviewer) */}
      {remoteVideoTrack && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* Speaker/Volume control button - REMOVED as per user request */}

      {/* AI Avatar placeholder when no video */}
      {!remoteVideoTrack && (
        <>
          {/* Animated tech background */}
          <div className="absolute inset-0">
            <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice">
              <defs>
                <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#818cf8" stopOpacity="0.3" />
                </linearGradient>
              </defs>
              <g stroke="url(#lineGrad)" strokeWidth="0.8" fill="none">
                <path d="M0,80 H180 L200,100 H380 L400,80 H560" className="animate-pulse" />
                <path d="M800,120 H620 L600,140 H420 L400,120 H240" className="animate-pulse" style={{ animationDelay: '0.3s' }} />
                <path d="M0,200 H140 L160,220 H300" className="animate-pulse" style={{ animationDelay: '0.6s' }} />
                <path d="M800,280 H660 L640,260 H500" className="animate-pulse" style={{ animationDelay: '0.9s' }} />
                <path d="M0,360 H200 L220,380 H400 L420,360 H580" className="animate-pulse" style={{ animationDelay: '1.2s' }} />
                <path d="M800,440 H600 L580,460 H400 L380,440 H200" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
                <path d="M0,520 H260 L280,540 H480" className="animate-pulse" style={{ animationDelay: '1.8s' }} />
                <circle cx="180" cy="80" r="4" fill="#60a5fa" className="animate-pulse" />
                <circle cx="400" cy="80" r="4" fill="#818cf8" className="animate-pulse" style={{ animationDelay: '0.2s' }} />
                <circle cx="600" cy="140" r="4" fill="#60a5fa" className="animate-pulse" style={{ animationDelay: '0.4s' }} />
                <circle cx="400" cy="120" r="4" fill="#a78bfa" className="animate-pulse" style={{ animationDelay: '0.6s' }} />
                <circle cx="140" cy="200" r="4" fill="#818cf8" className="animate-pulse" style={{ animationDelay: '0.8s' }} />
                <circle cx="660" cy="280" r="4" fill="#60a5fa" className="animate-pulse" style={{ animationDelay: '1s' }} />
                <circle cx="420" cy="360" r="4" fill="#a78bfa" className="animate-pulse" style={{ animationDelay: '1.2s' }} />
                <circle cx="380" cy="440" r="4" fill="#818cf8" className="animate-pulse" style={{ animationDelay: '1.4s' }} />
              </g>
            </svg>
          </div>

          {/* AI Avatar Icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="relative"
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-indigo-500/30 rounded-full blur-3xl scale-150" />

              {/* Professional AI Interviewer Icon */}
              <div className="relative w-40 h-40 flex items-center justify-center">
                <svg viewBox="0 0 120 120" className="w-full h-full drop-shadow-2xl">
                  <defs>
                    <linearGradient id="aiGradInterview" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3b82f6" />
                      <stop offset="50%" stopColor="#6366f1" />
                      <stop offset="100%" stopColor="#8b5cf6" />
                    </linearGradient>
                    <linearGradient id="innerGradInterview" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#1e3a5f" />
                      <stop offset="100%" stopColor="#1e1b4b" />
                    </linearGradient>
                    <filter id="glowInterview">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Outer rings */}
                  <circle cx="60" cy="60" r="55" fill="none" stroke="url(#aiGradInterview)" strokeWidth="2" opacity="0.6" />
                  <circle cx="60" cy="60" r="50" fill="none" stroke="url(#aiGradInterview)" strokeWidth="1" opacity="0.4" />

                  {/* Main head shape */}
                  <circle cx="60" cy="55" r="38" fill="url(#innerGradInterview)" />

                  {/* AI Brain pattern */}
                  <g filter="url(#glowInterview)" opacity="0.9">
                    {/* Central processor */}
                    <rect x="50" y="45" width="20" height="20" rx="3" fill="none" stroke="#60a5fa" strokeWidth="1.5" />
                    <rect x="54" y="49" width="12" height="12" rx="2" fill="#3b82f6" opacity="0.6" />

                    {/* Neural connections */}
                    <path d="M50,55 L35,45" stroke="#60a5fa" strokeWidth="1" />
                    <path d="M50,55 L35,65" stroke="#60a5fa" strokeWidth="1" />
                    <path d="M70,55 L85,45" stroke="#60a5fa" strokeWidth="1" />
                    <path d="M70,55 L85,65" stroke="#60a5fa" strokeWidth="1" />
                    <path d="M60,45 L60,32" stroke="#60a5fa" strokeWidth="1" />
                    <path d="M60,65 L60,78" stroke="#60a5fa" strokeWidth="1" />

                    {/* Neural nodes */}
                    <circle cx="35" cy="45" r="4" fill="#818cf8" />
                    <circle cx="35" cy="65" r="4" fill="#818cf8" />
                    <circle cx="85" cy="45" r="4" fill="#818cf8" />
                    <circle cx="85" cy="65" r="4" fill="#818cf8" />
                    <circle cx="60" cy="32" r="4" fill="#818cf8" />
                    <circle cx="60" cy="78" r="4" fill="#818cf8" />
                  </g>

                  {/* Microphone icon at bottom */}
                  <g transform="translate(52, 88)">
                    <rect x="4" y="0" width="8" height="14" rx="4" fill="none" stroke="#60a5fa" strokeWidth="1.5" />
                    <path d="M0,10 Q0,18 8,18 Q16,18 16,10" fill="none" stroke="#60a5fa" strokeWidth="1.5" />
                    <line x1="8" y1="18" x2="8" y2="24" stroke="#60a5fa" strokeWidth="1.5" />
                    <line x1="4" y1="24" x2="12" y2="24" stroke="#60a5fa" strokeWidth="1.5" />
                  </g>

                  {/* Animated pulse ring */}
                  <circle cx="60" cy="55" r="42" fill="none" stroke="#60a5fa" strokeWidth="0.5" opacity="0.3" className="animate-ping" style={{ animationDuration: '2s' }} />
                </svg>
              </div>

              {/* AI Interviewer Label */}
              <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                <span className="text-blue-400 text-sm font-medium tracking-wider">الذكاء الإصطناعي</span>
              </div>
            </motion.div>
          </div>

          {/* Floating particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-blue-400/50 rounded-full"
                initial={{
                  x: Math.random() * 100 + '%',
                  y: Math.random() * 100 + '%',
                  opacity: 0,
                }}
                animate={{
                  y: [null, '-20%'],
                  opacity: [0, 0.7, 0],
                }}
                transition={{
                  duration: 4 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 3,
                  ease: 'linear',
                }}
              />
            ))}
          </div>
        </>
      )}

      {/* Name label */}
      <div className="absolute bottom-6 start-6">
        <div className="flex items-center gap-3 px-4 py-2 bg-black/50 backdrop-blur-sm rounded-full">
          <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
          <span className="text-white font-medium">الذكاء الإصطناعي</span>
        </div>
      </div>
    </div>
  );
};

// Transcript Panel Component
// const TranscriptPanel = ({ messages }) => {
//   const scrollRef = useRef(null);
//   const text = {
//     title: 'النص',
//     notes: 'ملاحظاتي',
//     today: 'اليوم',
//     noMessages: 'لم تبدأ المحادثة بعد...',
//   };

//   // Auto-scroll to bottom on new messages
//   useEffect(() => {
//     if (scrollRef.current) {
//       scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
//     }
//   }, [messages]);

//   return (
//     <div className="h-full flex flex-col bg-white rounded-2xl overflow-hidden">
//       {/* Tabs */}
//       <div className="flex border-b border-gray-100">
//         <button className="flex-1 px-4 py-3 text-sm font-medium text-primary-600 border-b-2 border-primary-500 flex items-center justify-center gap-2">
//           <TranscriptIcon size={16} />
//           {text.title}
//         </button>
//         <button className="flex-1 px-4 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 flex items-center justify-center gap-2">
//           <NotesIcon size={16} />
//           {text.notes}
//         </button>
//       </div>

//       {/* Date header */}
//       <div className="px-4 py-2 text-center">
//         <span className="text-xs text-gray-400">{text.today}</span>
//       </div>

//       {/* Messages */}
//       <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-2 space-y-4">
//         {messages.length === 0 ? (
//           <div className="text-center text-gray-400 py-8">
//             {text.noMessages}
//           </div>
//         ) : (
//           messages.map((msg, index) => (
//             <motion.div
//               key={index}
//               initial={{ opacity: 0, y: 10 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: index * 0.1 }}
//               className={`${msg.sender === 'ai' ? 'pe-8' : 'ps-8'}`}
//             >
//               <div
//                 className={`
//                   p-4 rounded-2xl max-w-[90%]
//                   ${msg.sender === 'ai'
//                     ? 'bg-gray-50 text-gray-800 rounded-ss-sm'
//                     : 'bg-primary-50 text-gray-800 rounded-se-sm ms-auto'
//                   }
//                 `}
//               >
//                 <p className="text-sm leading-relaxed">{msg.text}</p>
//                 <span className="text-xs text-gray-400 mt-2 block">
//                   {msg.timestamp}
//                 </span>
//               </div>
//             </motion.div>
//           ))
//         )}
//       </div>
//     </div>
//   );
// };

// Control Bar Component
const ControlBar = ({
  time,
  interviewType,
  micEnabled,
  cameraEnabled,
  onToggleMic,
  onToggleCamera,
  onEndInterview,
  onSettings,
}) => {
  return (
    <div className="bg-white/95 backdrop-blur-sm border-t border-gray-100 px-3 sm:px-6 py-3 sm:py-4">
      <div className="flex items-center justify-center sm:justify-between max-w-7xl mx-auto">
        {/* Time & Interview Type - Hidden on mobile */}
        <div className="hidden sm:flex items-center gap-4">
          <span className="text-gray-700 font-medium">
            {time}
          </span>
          <span className="text-gray-400">|</span>
          <span className="text-gray-600">
            {interviewType}
          </span>
        </div>

        {/* Center Controls */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Microphone Toggle - REMOVED as per user request */}

          {/* Camera Toggle */}
          <button
            onClick={onToggleCamera}
            className={`
              p-3 sm:p-4 rounded-full transition-all duration-200
              ${cameraEnabled
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                : 'bg-red-100 text-red-600 hover:bg-red-200'
              }
            `}
          >
            {cameraEnabled ? <CameraIcon size={20} className="sm:w-6 sm:h-6" /> : <CameraOffIcon size={20} className="sm:w-6 sm:h-6" />}
          </button>

          {/* End Interview */}
          <button
            onClick={onEndInterview}
            className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all duration-200 font-medium text-sm sm:text-base"
          >
            <EndCallIcon size={18} className="sm:w-5 sm:h-5" />
            <span className="hidden xs:inline">إنهاء المقابلة</span>
            <span className="xs:hidden">إنهاء</span>
          </button>
        </div>

        {/* Right Controls */}
        {/* <div className="flex items-center gap-3">
          <button
            onClick={onSettings}
            className="p-3 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all"
          >
            <SettingsIcon size={20} />
          </button>
          <button className="p-3 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div> */}
      </div>
    </div>
  );
};

// Main Interview Page
const InterviewPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, actions } = useInterview();
  const { isOnline, quality } = useNetwork();

  // Local camera stream (for display only, not for recording)
  const [localStream, setLocalStream] = useState(null);

  // Keep ref in sync with state
  useEffect(() => {
    localStreamRef.current = localStream;
  }, [localStream]);

  // LiveKit integration
  const {
    room,
    connect: connectLiveKit,
    disconnect: disconnectLiveKit,
    isConnecting: isConnectingLiveKit,
    isConnected: isConnectedLiveKit,
    error: livekitError,
    remoteVideoTrack,
    remoteAudioTrack,
    toggleMicrophone: livekitToggleMic,
    enableMicrophone: livekitEnableMic,
    waitForAgentReady,
    waitForAvatarVideo,
    waitForAvatarToStopSpeaking,
    startInterviewSignal,
    sendDataMessage,
    avatarSpeaking,
    agentState,
    interviewComplete,
    isRoomConnected,
  } = useLiveKit();

  // Combined stream for recording: interviewee main + avatar PiP + mixed audio (both voices)
  const { combinedStream, isReady: isCombinedStreamReady } = useCombinedStream({
    localStream,
    remoteVideoTrack,
    remoteAudioTrack,
  });

  // Recorder uses combined stream when available, falls back to local stream
  // (ensures recording works even when avatar video fails to load)
  const recordingStream = combinedStream || localStream;
  const { startRecording, stopRecording, isRecording, getCurrentBlob, getTotalBytes, sliceByteRange, chunks: recordedChunks, mimeType: recorderMimeType } = useRecorder(recordingStream);

  const [showEndModal, setShowEndModal] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [toast, setToast] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const [interviewMetadata, setInterviewMetadata] = useState(null);
  const [candidateId, setCandidateId] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [uploadProgress, setUploadProgress] = useState({ stage: 'waiting', progress: 0, current: 0, total: 0 });

  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const localStreamRef = useRef(null);
  const hasStartedRecordingRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const cleanupTimeoutRef = useRef(null);
  const streamingUploaderRef = useRef(null);
  const lastUploadedBytesRef = useRef(0);
  const uploadIntervalRef = useRef(null);
  const isStreamingUploadingRef = useRef(false);
  const streamingDoneRef = useRef(false);

  // Text content
  const text = {
    endTitle: 'إنهاء المقابلة',
    endMessage: 'هل أنت متأكد من إنهاء المقابلة؟ سيتم حفظ التسجيل.',
    endYes: 'نعم، أنهِ',
    endNo: 'لا، استمر',
    saving: 'جاري حفظ المقابلة...',
    connectionLost: 'انقطع الاتصال. جاري إعادة المحاولة...',
    technicalInterview: 'مقابلة تقنية',
    you: 'أنت',
  };

  // Initialize interview and connect to LiveKit
  useEffect(() => {
    // Cancel any pending cleanup from previous unmount (StrictMode remount)
    if (cleanupTimeoutRef.current) {
      console.log('InterviewPage: Cancelling pending cleanup (StrictMode remount detected)');
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    const initializeInterview = async () => {
      // Skip if already connected (React StrictMode remount)
      if (isConnectedLiveKit) {
        console.log('InterviewPage: Already connected to LiveKit, skipping initialization');
        setIsInitializing(false);
        return;
      }

      // Prevent double initialization within same mount cycle
      if (hasInitializedRef.current) {
        console.log('InterviewPage: Already initialized, skipping...');
        return;
      }
      hasInitializedRef.current = true;

      try {
        setIsInitializing(true);

        // Get candidate ID from interview context, URL params, or sessionStorage
        let candidateIdValue =
          state.interviewData?.candidateId ||
          searchParams.get('candidate_id') ||
          sessionStorage.getItem('candidateId');

        // Get interview ID from interview context, URL params, or sessionStorage
        let interviewIdValue =
          state.interviewData?.interviewId ||
          searchParams.get('interview_id') ||
          sessionStorage.getItem('interviewId');

        console.log('InterviewPage: Initializing with candidateId:', candidateIdValue);
        console.log('InterviewPage: Initializing with interviewId:', interviewIdValue);
        console.log('InterviewPage: State interviewData:', state.interviewData);
        console.log('InterviewPage: URL params candidate_id:', searchParams.get('candidate_id'));
        console.log('InterviewPage: URL params interview_id:', searchParams.get('interview_id'));
        console.log('InterviewPage: SessionStorage candidateId:', sessionStorage.getItem('candidateId'));
        console.log('InterviewPage: SessionStorage interviewId:', sessionStorage.getItem('interviewId'));

        // Debug: Check if tokens are available
        const debugAccessToken = tokenStorage.getAccessToken();
        const debugEncryptedToken = tokenStorage.getEncryptedToken();
        console.log('InterviewPage: Token check - hasAccessToken:', !!debugAccessToken, 'hasEncryptedToken:', !!debugEncryptedToken);

        if (!candidateIdValue) {
          console.error('InterviewPage: No candidate ID found');
          setIsInitializing(false);
          setToast({
            type: 'error',
            message: 'لم يتم العثور على معرف المرشح. الرجاء المحاولة مرة أخرى.',
          });
          // Don't redirect immediately, give user time to see error
          setTimeout(() => {
            navigate(ROUTES.PROCEED, { replace: true });
          }, 3000);
          return;
        }

        candidateIdValue = parseInt(candidateIdValue);
        setCandidateId(candidateIdValue);

        // Parse interviewId if present
        if (interviewIdValue) {
          interviewIdValue = parseInt(interviewIdValue);
        }

        // Check for stored room name (for reconnection after refresh - same as connection loss)
        const storedRoomName = sessionStorage.getItem(STORAGE_KEYS.ACTIVE_ROOM);
        const isReconnect = !!storedRoomName;
        console.log('InterviewPage: Reconnect mode:', isReconnect, 'storedRoom:', storedRoomName);

        // Get LiveKit metadata
        // If reconnecting, pass the stored room name to get token for the SAME room (like connection loss)
        console.log('InterviewPage: Fetching LiveKit metadata for candidate:', candidateIdValue, 'interview:', interviewIdValue, 'room:', storedRoomName);
        let metadata;
        try {
          metadata = await interviewService.getInterviewMetadata(candidateIdValue, interviewIdValue, storedRoomName);
          setInterviewMetadata(metadata);
          console.log('InterviewPage: LiveKit metadata:', metadata);

          // Save room name for potential reconnection (like connection loss auto-save)
          if (metadata.room) {
            sessionStorage.setItem(STORAGE_KEYS.ACTIVE_ROOM, metadata.room);
            console.log('InterviewPage: Saved room name for reconnection:', metadata.room);
          }
        } catch (metadataError) {
          console.error('InterviewPage: Failed to fetch metadata:', metadataError);
          throw new Error(`فشل في جلب بيانات المقابلة: ${metadataError.message || 'خطأ في الشبكة'}`);
        }

        // Start local camera for recording (NOT for LiveKit)
        console.log('InterviewPage: Starting local camera...');
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true
          });
          setLocalStream(stream);
          console.log('InterviewPage: Local camera started');
        } catch (err) {
          console.error('InterviewPage: Failed to start camera:', err);
          // Continue anyway - camera not critical for LiveKit connection
        }

        // Connect to LiveKit room
        console.log('InterviewPage: Connecting to LiveKit room...');
        await connectLiveKit(metadata.url, metadata.token);
        console.log('InterviewPage: Connected to LiveKit successfully');

        // Unlock audio context (required for autoplay)
        try {
          const audioCtx = new (window.AudioContext || window.webkitAudioContext || function() {})();
          if (audioCtx && audioCtx.resume) {
            await audioCtx.resume();
            console.log('InterviewPage: Audio context unlocked');
          }
        } catch (e) {
          console.log('InterviewPage: Audio context not needed or unavailable');
        }

        // Start backend interview (dispatches agent to SAME room we connected to)
        // IMPORTANT: Use actual connected room name from LiveKit, not metadata
        // Use interviewIdValue (selected interview's candidate_id) when available
        const effectiveCandidateId = interviewIdValue || candidateIdValue;
        const actualRoomName = room?.name || metadata.room;
        console.log('InterviewPage: Starting interview on backend with room:', actualRoomName, 'effectiveCandidateId:', effectiveCandidateId);
        let startResult;
        try {
          startResult = await interviewService.startInterview(effectiveCandidateId, actualRoomName);
          console.log('InterviewPage: Backend interview started:', startResult);
        } catch (startError) {
          console.error('InterviewPage: Failed to start interview:', startError);
          throw new Error(`فشل في بدء المقابلة: ${startError.message || 'خطأ في الاتصال بالخادم'}`);
        }

        // Enable microphone IMMEDIATELY (keep connection alive)
        console.log('InterviewPage: Enabling microphone...');
        await livekitEnableMic();

        // Note: Removed "تم الاتصال بنجاح" success toast as per user request

        // Wait for agent ready signal (6s timeout like interview.html)
        console.log('InterviewPage: Waiting for agent ready signal...');
        const ready = await waitForAgentReady(6000);

        if (ready) {
          console.log('InterviewPage: Agent confirmed ready');
          // Small delay to ensure data channel is fully established
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          console.warn('InterviewPage: Agent ready timeout (6s) - proceeding anyway');
          // Wait longer if agent timed out
          await new Promise(resolve => setTimeout(resolve, 1500));
        }

        // Wait for avatar video to be ready (so lip sync works from greeting start)
        console.log('InterviewPage: Waiting for avatar video track...');
        const avatarReady = await waitForAvatarVideo(15000); // 15s timeout

        if (avatarReady) {
          console.log('InterviewPage: Avatar video ready - lip sync will work');
        } else {
          console.warn('InterviewPage: Avatar video timeout (15s) - starting without visual');
        }

        // Wait for room to be fully connected before sending signals
        console.log('InterviewPage: Waiting for room to be fully connected...');
        let roomConnectRetries = 0;
        const maxRoomConnectRetries = 10;
        while (!isRoomConnected() && roomConnectRetries < maxRoomConnectRetries) {
          console.log(`InterviewPage: Room not connected yet, waiting... (${roomConnectRetries + 1}/${maxRoomConnectRetries})`);
          await new Promise(resolve => setTimeout(resolve, 500));
          roomConnectRetries++;
        }

        if (!isRoomConnected()) {
          console.error('InterviewPage: Room failed to connect after retries');
          throw new Error('فشل في الاتصال بغرفة المقابلة');
        }

        console.log('InterviewPage: Room is connected, sending start_interview signals...');

        // Send start_interview signals (multiple attempts like interview.html)
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            const success = await startInterviewSignal();
            if (success) {
              console.log(`InterviewPage: start_interview signal sent successfully (attempt ${attempt}/3)`);
            } else {
              console.warn(`InterviewPage: start_interview signal failed (attempt ${attempt}/3)`);
            }
            if (attempt < 3) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          } catch (err) {
            console.warn(`InterviewPage: Failed to send signal (attempt ${attempt}/3):`, err.message);
          }
        }
        console.log('InterviewPage: start_interview signals sent - agent should begin speaking');

        // Start timer
        timerRef.current = setInterval(() => {
          setElapsedTime((prev) => prev + 1);
        }, 1000);

        console.log('InterviewPage: Initialization complete - agent should start speaking');
        setIsInitializing(false);
      } catch (error) {
        console.error('InterviewPage: Failed to initialize interview:', error);
        setIsInitializing(false);
        setToast({
          type: 'error',
          message: error.message || 'فشل في الاتصال بالمقابلة. الرجاء المحاولة مرة أخرى.',
        });

        // Navigate back to proceed page after delay
        setTimeout(() => {
          navigate(ROUTES.PROCEED, { replace: true });
        }, 5000);
      }
    };

    initializeInterview();

    return () => {
      console.log('InterviewPage: Cleanup on unmount');

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Delayed cleanup - allows StrictMode remount to cancel it
      // If component remounts within 500ms (StrictMode), cleanup is cancelled
      // If it's a real navigation away, cleanup runs after delay
      cleanupTimeoutRef.current = setTimeout(() => {
        console.log('InterviewPage: Delayed cleanup executing (real unmount)');

        // Stop local stream first (turn off camera LED)
        if (localStreamRef.current) {
          console.log('InterviewPage: Cleanup - stopping local stream tracks');
          localStreamRef.current.getTracks().forEach(track => {
            track.stop();
            console.log('InterviewPage: Cleanup - stopped track:', track.kind, track.label);
          });
          localStreamRef.current = null;
        }

        // Disconnect LiveKit (this also stops any LiveKit-created tracks)
        disconnectLiveKit();
      }, 500);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Start recording when a stream is available.
  // Prefers combinedStream (avatar + local), falls back to localStream (camera only).
  // This ensures recording works even when avatar video fails to load.
  useEffect(() => {
    if (hasStartedRecordingRef.current) return;

    if (isCombinedStreamReady && combinedStream) {
      console.log('InterviewPage: Starting recording with combined stream (interviewee main + avatar PiP)...');
      console.log('InterviewPage: Combined stream tracks:', {
        videoTracks: combinedStream.getVideoTracks().length,
        audioTracks: combinedStream.getAudioTracks().length,
      });
      hasStartedRecordingRef.current = true;
      startRecording();
      console.log('InterviewPage: Recording started (combined stream)');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCombinedStreamReady, combinedStream]);

  // Fallback: start recording with local stream if combined stream is not ready
  // after initialization completes (avatar video failed/timed out)
  useEffect(() => {
    if (hasStartedRecordingRef.current) return;
    if (!localStream || isInitializing) return;

    // Combined stream not ready but we're past initialization — avatar failed
    if (!isCombinedStreamReady) {
      console.log('InterviewPage: Combined stream not available — starting recording with local stream (fallback)');
      console.log('InterviewPage: Local stream tracks:', {
        videoTracks: localStream.getVideoTracks().length,
        audioTracks: localStream.getAudioTracks().length,
      });
      hasStartedRecordingRef.current = true;
      startRecording();
      console.log('InterviewPage: Recording started (local stream fallback)');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStream, isInitializing, isCombinedStreamReady]);

  // Streaming upload: upload chunks every 10 seconds during recording
  // Uses getTotalBytes() + sliceByteRange() — never reconstructs the full blob
  // Uses isStreamingUploadingRef as mutex to prevent concurrent uploads
  // Uses streamingDoneRef to yield to final upload when interview ends
  useEffect(() => {
    if (!isRecording || !candidateId) return;

    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB per chunk (S3 minimum part size)
    const UPLOAD_INTERVAL_MS = 10000; // Upload every 10 seconds
    // Realistic estimate: 15-min interview at ~2.6 Mbps ≈ 290MB ≈ 58 chunks
    const ESTIMATED_MAX_CHUNKS = 60;

    // Reset streaming state on new recording
    streamingDoneRef.current = false;

    const uploadNewChunks = async () => {
      // Guard: skip if final upload has taken over or another upload is in-flight
      if (streamingDoneRef.current || isStreamingUploadingRef.current) return;

      // Cheap check — no blob construction
      const totalRecorded = getTotalBytes();
      if (totalRecorded === 0) {
        console.log('Streaming upload: No data yet');
        return;
      }

      const lastUploaded = lastUploadedBytesRef.current;

      // Only upload complete 5MB chunks (S3 min part size)
      const startChunk = Math.floor(lastUploaded / CHUNK_SIZE);
      const endChunk = Math.floor(totalRecorded / CHUNK_SIZE);

      if (startChunk >= endChunk) {
        console.log(`Streaming upload: Not enough for a full chunk yet (${totalRecorded - lastUploaded} bytes new)`);
        return;
      }

      // Set mutex
      isStreamingUploadingRef.current = true;

      try {
        // Initialize upload session on first upload
        if (!streamingUploaderRef.current) {
          const authTokens = {
            accessToken: tokenStorage.getAccessToken(),
            encryptedToken: tokenStorage.getEncryptedToken(),
          };

          console.log('Streaming upload: Initializing with estimated size...');
          const uploader = new VideoUploadManager(candidateId, authTokens);

          await uploader.initialize({
            size: ESTIMATED_MAX_CHUNKS * CHUNK_SIZE,
            type: 'video/webm',
          });

          streamingUploaderRef.current = uploader;
          console.log('Streaming upload: Session initialized, upload_id:', uploader.uploadId);
        }

        const uploader = streamingUploaderRef.current;

        console.log(`Streaming upload: Uploading chunks ${startChunk} to ${endChunk - 1}...`);

        for (let i = startChunk; i < endChunk; i++) {
          if (streamingDoneRef.current) {
            console.log('Streaming upload: Final upload taking over, stopping');
            break;
          }

          const byteStart = i * CHUNK_SIZE;
          const byteEnd = byteStart + CHUNK_SIZE; // always exactly 5MB during streaming

          // sliceByteRange only touches the overlapping MediaRecorder chunks
          const chunkBlob = sliceByteRange(byteStart, byteEnd);
          if (!chunkBlob || chunkBlob.size === 0) break;

          console.log(`Streaming upload: Uploading chunk ${i} (${chunkBlob.size} bytes)...`);
          await uploader.uploadChunkWithRetry(i, chunkBlob, 2);
          lastUploadedBytesRef.current = byteEnd;
          console.log(`Streaming upload: Chunk ${i} uploaded`);
        }
      } catch (err) {
        console.error('Streaming upload: Failed to upload chunks:', err);
        // Non-fatal — continue recording, we'll upload everything at the end
      } finally {
        isStreamingUploadingRef.current = false;
      }
    };

    // Start periodic upload
    uploadIntervalRef.current = setInterval(uploadNewChunks, UPLOAD_INTERVAL_MS);
    console.log('Streaming upload: Started interval');

    return () => {
      if (uploadIntervalRef.current) {
        clearInterval(uploadIntervalRef.current);
        uploadIntervalRef.current = null;
        console.log('Streaming upload: Stopped interval');
      }
    };
  }, [isRecording, candidateId, getTotalBytes, sliceByteRange]);

  // Attach remote audio track
  useEffect(() => {
    if (remoteAudioTrack && audioRef.current) {
      remoteAudioTrack.attach(audioRef.current);
      return () => {
        remoteAudioTrack.detach();
      };
    }
  }, [remoteAudioTrack]);

  // Auto-end interview when agent state becomes "finished" (closing statement done)
  useEffect(() => {
    if (interviewComplete && !isEnding) {
      console.log('InterviewPage: Agent state is FINISHED - auto-ending interview');

      // Small delay to ensure avatar audio is fully done
      const timeoutId = setTimeout(() => {
        if (!isEnding) {
          autoEndInterview();
        }
      }, 1500); // 1.5 second delay after "finished" state

      return () => clearTimeout(timeoutId);
    }
  }, [interviewComplete, isEnding]);

  // Auto-end interview function (no user interaction required)
  const autoEndInterview = async () => {
    if (isEnding) return;

    console.log('InterviewPage: Auto-ending interview...');
    setIsEnding(true);

    try {
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Store room name BEFORE disconnecting (disconnect clears room)
      const roomName = room?.name;
      console.log('InterviewPage: Stored room name before disconnect:', roomName);

      // Stop recording and get video blob
      let videoBlob = null;
      if (isRecording) {
        const recordingResult = await stopRecording();
        // stopRecording returns { blob, chunks, duration, mimeType }
        videoBlob = recordingResult?.blob;
        console.log('InterviewPage: Auto-end recording stopped, result:', {
          hasResult: !!recordingResult,
          hasBlob: !!videoBlob,
          blobSize: videoBlob?.size,
          chunksCount: recordingResult?.chunks?.length,
          duration: recordingResult?.duration,
        });
        if (!videoBlob && getCurrentBlob) {
          videoBlob = getCurrentBlob();
          console.log('InterviewPage: Using fallback getCurrentBlob, size:', videoBlob?.size);
        }
      } else {
        // Not recording, but try to get any collected chunks
        if (getCurrentBlob) {
          videoBlob = getCurrentBlob();
          console.log('InterviewPage: Auto-end not recording, using getCurrentBlob, size:', videoBlob?.size);
        }
      }

      // Stop and fully release all media streams to turn off camera LED
      console.log('InterviewPage: Stopping all media streams on auto-end...');
      const autoStreams = [localStreamRef.current, localStream, combinedStream].filter(Boolean);
      [...new Set(autoStreams)].forEach(s => {
        s.getTracks().forEach(track => {
          track.stop();
          s.removeTrack(track);
          console.log(`Auto-end: Stopped + removed ${track.kind} track`);
        });
      });
      localStreamRef.current = null;

      // Clear state immediately so VideoPreview unmounts
      setLocalStream(null);

      // Disconnect from LiveKit (stops LiveKit's mic track)
      await disconnectLiveKit();

      // Final cleanup: clear ALL video/audio elements on the page
      try {
        document.querySelectorAll('video, audio').forEach(el => {
          if (el.srcObject) {
            el.srcObject.getTracks().forEach(t => { t.stop(); el.srcObject.removeTrack(t); });
            el.srcObject = null;
          }
          el.pause();
          el.removeAttribute('src');
          el.load();
        });
        console.log('Auto-end: Cleared all video/audio elements');
      } catch (e) {
        console.warn('Auto-end: Error cleaning up media elements:', e);
      }

      console.log('Auto-end: Video blob size:', videoBlob?.size);

      // Signal streaming upload to stop, then wait for any in-flight upload to finish
      streamingDoneRef.current = true;
      if (uploadIntervalRef.current) {
        clearInterval(uploadIntervalRef.current);
        uploadIntervalRef.current = null;
      }
      let drainAttempts = 0;
      while (isStreamingUploadingRef.current && drainAttempts < 50) {
        await new Promise(resolve => setTimeout(resolve, 100));
        drainAttempts++;
      }
      if (drainAttempts > 0) {
        console.log(`Auto-end: Waited ${drainAttempts * 100}ms for streaming upload to drain`);
      }

      // Upload video - use streaming uploader if available, otherwise create new one
      if (videoBlob && videoBlob.size > 0 && candidateId) {
        const CHUNK_SIZE = 5 * 1024 * 1024;
        const totalChunks = Math.ceil(videoBlob.size / CHUNK_SIZE);

        console.log('InterviewPage (auto-end): Starting video upload...', {
          blobSize: videoBlob.size,
          blobType: videoBlob.type,
          candidateId,
          hasStreamingUploader: !!streamingUploaderRef.current,
          lastUploadedBytes: lastUploadedBytesRef.current,
          totalChunks,
        });

        // Progress starts at 1/(totalChunks+1), never 0%
        const progressDivisor = totalChunks + 1;
        const initialProgress = Math.round((1 / progressDivisor) * 100);
        setUploadProgress({ stage: 'uploading', progress: initialProgress, current: 0, total: totalChunks });

        try {
          const authTokens = {
            accessToken: tokenStorage.getAccessToken(),
            encryptedToken: tokenStorage.getEncryptedToken(),
          };

          let uploader = streamingUploaderRef.current;
          let startChunk = 0;
          const usedStreamingUploader = !!(uploader && uploader.uploadId);

          if (usedStreamingUploader) {
            startChunk = Math.floor(lastUploadedBytesRef.current / CHUNK_SIZE);
            console.log(`Auto-end: Using streaming uploader, starting from chunk ${startChunk}`);
          } else {
            console.log('Auto-end: Creating new uploader');
            uploader = new VideoUploadManager(candidateId, authTokens);
            await uploader.initialize(videoBlob);
          }

          // Upload remaining chunks in parallel batches of 3
          const PARALLEL = uploader.PARALLEL_LIMIT || 3;
          for (let batchStart = startChunk; batchStart < totalChunks; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const batch = [];

            for (let i = batchStart; i < batchEnd; i++) {
              const start = i * CHUNK_SIZE;
              const end = Math.min(start + CHUNK_SIZE, videoBlob.size);
              const chunkBlob = videoBlob.slice(start, end);
              batch.push(uploader.uploadChunkWithRetry(i, chunkBlob, 3));
            }

            await Promise.all(batch);
            const progress = Math.round(((batchEnd + 1) / progressDivisor) * 100);
            setUploadProgress({ stage: 'uploading', progress, current: batchEnd, total: totalChunks });
          }

          // Correct total_chunks on backend before completing.
          // If update fails (race with auto-finalization), fall back to allow_partial.
          let chunksUpdated = false;
          if (usedStreamingUploader) {
            chunksUpdated = await uploader.updateTotalChunks(totalChunks);
          }

          setUploadProgress({ stage: 'finalizing', progress: 100, current: totalChunks, total: totalChunks });
          await uploader.complete(usedStreamingUploader && !chunksUpdated);
          console.log('InterviewPage (auto-end): Upload completed successfully');

        } catch (uploadError) {
          console.error('InterviewPage (auto-end): Video upload failed:', uploadError);
          setToast({
            type: 'warning',
            message: 'تم حفظ المقابلة ولكن فشل رفع الفيديو.',
          });
        }
      }

      // End interview on backend (pass room name for proper session cleanup)
      if (candidateId) {
        console.log('InterviewPage: Calling endInterview with room:', roomName);
        await interviewService.endInterview(candidateId, roomName);
      }

      // Clear room name (interview is done, no need for reconnection)
      sessionStorage.removeItem(STORAGE_KEYS.ACTIVE_ROOM);

      // Update state
      actions.setPhase(INTERVIEW_PHASES.COMPLETED);
      actions.setInterviewData({
        interviewDuration: elapsedTime,
        completedAt: Date.now(),
      });

      // Small delay before navigation to ensure all cleanups complete
      console.log('InterviewPage: Waiting for cleanups to complete before navigation...');
      await new Promise(resolve => setTimeout(resolve, 500));

      navigate(ROUTES.COMPLETED);
    } catch (error) {
      console.error('Auto-end failed:', error);
      setToast({
        type: 'error',
        message: 'فشل في حفظ المقابلة. يرجى المحاولة يدوياً.',
      });
      setIsEnding(false);
    }
  };

  // Format elapsed time
  const formatElapsedTime = () => {
    const hours = Math.floor(elapsedTime / 3600);
    const minutes = Math.floor((elapsedTime % 3600) / 60);
    const seconds = elapsedTime % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Get current time
  const getCurrentTime = () => {
    const now = new Date();
    return now.toLocaleTimeString('ar-EG', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Add transcript message
  const addTranscriptMessage = (sender, text) => {
    const now = new Date();
    const timestamp = now.toLocaleTimeString('ar-EG', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    setTranscript(prev => [...prev, {
      sender,
      text,
      timestamp,
    }]);

    // Also add to context
    actions.addTranscriptMessage({
      sender,
      text,
      timestamp: Date.now(),
    });
  };

  // Toggle camera - toggle local video track
  const handleToggleCamera = async () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        actions.setMediaState({ cameraEnabled: videoTrack.enabled });
      }
    }
  };

  // Toggle microphone - use LiveKit when connected
  const handleToggleMic = async () => {
    if (isConnectedLiveKit && livekitToggleMic) {
      const newState = await livekitToggleMic();
      actions.setMediaState({ microphoneEnabled: newState });
    }
  };

  // Handle end interview
  const handleEndInterview = () => {
    setShowEndModal(true);
  };

  // Confirm end interview
  const confirmEndInterview = async () => {
    setShowEndModal(false);
    setIsEnding(true);

    try {
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Send end_interview signal to agent via LiveKit data channel
      // This triggers the agent to generate the assessment and say closing statement
      console.log('InterviewPage: Sending end_interview signal to agent...');
      const endSignalSent = await sendDataMessage({ type: 'end_interview' });

      if (endSignalSent) {
        console.log('InterviewPage: end_interview signal sent - waiting for avatar to finish closing statement...');

        // Wait for avatar to finish speaking the closing statement
        // Timeout after 30 seconds, silence threshold of 2 seconds
        const avatarFinished = await waitForAvatarToStopSpeaking(30000, 2000);

        if (avatarFinished) {
          console.log('InterviewPage: Avatar finished closing statement');
        } else {
          console.warn('InterviewPage: Avatar speaking timeout - proceeding anyway');
        }
      } else {
        console.warn('InterviewPage: Failed to send end_interview signal - proceeding with cleanup');
      }

      // Now stop recording and get video blob (after avatar finished speaking)
      let videoBlob = null;
      if (isRecording) {
        const recordingResult = await stopRecording();
        // stopRecording returns { blob, chunks, duration, mimeType }
        videoBlob = recordingResult?.blob;
        console.log('InterviewPage: Recording stopped, result:', {
          hasResult: !!recordingResult,
          hasBlob: !!videoBlob,
          blobSize: videoBlob?.size,
          chunksCount: recordingResult?.chunks?.length,
          duration: recordingResult?.duration,
        });

        // Fallback: try to get current blob if stopRecording didn't return it
        if (!videoBlob && getCurrentBlob) {
          videoBlob = getCurrentBlob();
          console.log('InterviewPage: Using fallback getCurrentBlob, size:', videoBlob?.size);
        }
      } else {
        // Not recording, but try to get any collected chunks
        if (getCurrentBlob) {
          videoBlob = getCurrentBlob();
          console.log('InterviewPage: Not recording, using getCurrentBlob, size:', videoBlob?.size);
        }
      }

      // Store room name BEFORE disconnecting (disconnect clears room)
      const roomName = room?.name;
      console.log('InterviewPage: Stored room name before disconnect:', roomName);

      // Stop ALL media streams to turn off camera/mic
      console.log('InterviewPage: Stopping all media streams on manual end...');
      console.log('InterviewPage: localStreamRef.current:', !!localStreamRef.current);
      console.log('InterviewPage: localStream state:', !!localStream);
      console.log('InterviewPage: combinedStream:', !!combinedStream);

      // Stop and fully release all media streams to turn off camera LED
      const streamsToRelease = [localStreamRef.current, localStream, combinedStream].filter(Boolean);
      const uniqueStreams = [...new Set(streamsToRelease)];

      uniqueStreams.forEach(s => {
        s.getTracks().forEach(track => {
          track.stop();
          s.removeTrack(track);
          console.log(`Manual-end: Stopped + removed ${track.kind} track`);
        });
      });
      localStreamRef.current = null;

      // Clear state immediately so VideoPreview unmounts
      setLocalStream(null);

      // Disconnect from LiveKit (stops LiveKit's mic track)
      await disconnectLiveKit();

      // Final cleanup: clear ALL video/audio elements on the page
      try {
        document.querySelectorAll('video, audio').forEach(el => {
          if (el.srcObject) {
            el.srcObject.getTracks().forEach(t => { t.stop(); el.srcObject.removeTrack(t); });
            el.srcObject = null;
          }
          el.pause();
          el.removeAttribute('src');
          el.load();
        });
        console.log('Manual-end: Cleared all video/audio elements');
      } catch (e) {
        console.warn('Manual-end: Error cleaning up media elements:', e);
      }

      console.log('InterviewPage: Video blob ready for upload, size:', videoBlob?.size);

      // Signal streaming upload to stop, then wait for any in-flight upload to finish
      streamingDoneRef.current = true;
      if (uploadIntervalRef.current) {
        clearInterval(uploadIntervalRef.current);
        uploadIntervalRef.current = null;
      }
      let manualDrainAttempts = 0;
      while (isStreamingUploadingRef.current && manualDrainAttempts < 50) {
        await new Promise(resolve => setTimeout(resolve, 100));
        manualDrainAttempts++;
      }
      if (manualDrainAttempts > 0) {
        console.log(`Manual end: Waited ${manualDrainAttempts * 100}ms for streaming upload to drain`);
      }

      // Upload video - use streaming uploader if available, otherwise create new one
      if (videoBlob && videoBlob.size > 0 && candidateId) {
        const CHUNK_SIZE = 5 * 1024 * 1024;
        const totalChunks = Math.ceil(videoBlob.size / CHUNK_SIZE);

        console.log('InterviewPage: Starting video upload...', {
          blobSize: videoBlob.size,
          blobType: videoBlob.type,
          candidateId,
          hasStreamingUploader: !!streamingUploaderRef.current,
          lastUploadedBytes: lastUploadedBytesRef.current,
          totalChunks,
        });

        // Progress starts at 1/(totalChunks+1), never 0%
        const progressDivisor = totalChunks + 1;
        const initialProgress = Math.round((1 / progressDivisor) * 100);
        setUploadProgress({ stage: 'uploading', progress: initialProgress, current: 0, total: totalChunks });

        try {
          const authTokens = {
            accessToken: tokenStorage.getAccessToken(),
            encryptedToken: tokenStorage.getEncryptedToken(),
          };

          let uploader = streamingUploaderRef.current;
          let startChunk = 0;
          const usedStreamingUploader = !!(uploader && uploader.uploadId);

          if (usedStreamingUploader) {
            startChunk = Math.floor(lastUploadedBytesRef.current / CHUNK_SIZE);
            console.log(`Manual end: Using streaming uploader, starting from chunk ${startChunk}`);
          } else {
            console.log('Manual end: Creating new uploader');
            uploader = new VideoUploadManager(candidateId, authTokens);
            await uploader.initialize(videoBlob);
          }

          // Upload remaining chunks in parallel batches of 3
          const PARALLEL = uploader.PARALLEL_LIMIT || 3;
          for (let batchStart = startChunk; batchStart < totalChunks; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const batch = [];

            for (let i = batchStart; i < batchEnd; i++) {
              const start = i * CHUNK_SIZE;
              const end = Math.min(start + CHUNK_SIZE, videoBlob.size);
              const chunkBlob = videoBlob.slice(start, end);
              batch.push(uploader.uploadChunkWithRetry(i, chunkBlob, 3));
            }

            await Promise.all(batch);
            const progress = Math.round(((batchEnd + 1) / progressDivisor) * 100);
            setUploadProgress({ stage: 'uploading', progress, current: batchEnd, total: totalChunks });
          }

          // Correct total_chunks on backend before completing.
          // If update fails (race with auto-finalization), fall back to allow_partial.
          let manualChunksUpdated = false;
          if (usedStreamingUploader) {
            manualChunksUpdated = await uploader.updateTotalChunks(totalChunks);
          }

          setUploadProgress({ stage: 'finalizing', progress: 100, current: totalChunks, total: totalChunks });
          await uploader.complete(usedStreamingUploader && !manualChunksUpdated);
          console.log('InterviewPage: Upload completed successfully');

        } catch (uploadError) {
          console.error('InterviewPage: Video upload failed:', uploadError);
          setToast({
            type: 'warning',
            message: 'تم حفظ المقابلة ولكن فشل رفع الفيديو. يمكنك المحاولة مرة أخرى من الصفحة التالية.',
          });
        }
      } else {
        console.warn('InterviewPage: No video blob available for upload', {
          hasBlob: !!videoBlob,
          blobSize: videoBlob?.size,
          hasCandidateId: !!candidateId,
        });
      }

      // End interview on backend (pass room name for proper session cleanup)
      if (candidateId) {
        console.log('InterviewPage: Calling endInterview with room:', roomName);
        await interviewService.endInterview(candidateId, roomName);
      }

      // Clear room name (interview is done, no need for reconnection)
      sessionStorage.removeItem(STORAGE_KEYS.ACTIVE_ROOM);

      // Update state
      actions.setPhase(INTERVIEW_PHASES.COMPLETED);
      actions.setInterviewData({
        interviewDuration: elapsedTime,
        completedAt: Date.now(),
      });

      // Small delay before navigation to ensure all cleanups complete
      console.log('InterviewPage: Waiting for cleanups to complete before navigation...');
      await new Promise(resolve => setTimeout(resolve, 500));

      navigate(ROUTES.COMPLETED);
    } catch (error) {
      console.error('Failed to end interview:', error);
      setToast({
        type: 'error',
        message: error.message || 'فشل في حفظ المقابلة. يرجى المحاولة مرة أخرى.',
      });
      setIsEnding(false);
    }
  };

  // Network status warning
  useEffect(() => {
    if (!isOnline) {
      setToast({
        type: 'warning',
        message: text.connectionLost,
      });
    }
  }, [isOnline]);

  // Show loading screen during initialization
  if (isInitializing) {
    return (
      <div
        className="min-h-screen bg-gray-100 flex flex-col items-center justify-center"
        dir="rtl"
      >
        {/* Toast */}
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}

        <div className="bg-white rounded-2xl p-8 text-center shadow-strong max-w-md">
          <Spinner size="lg" className="mb-4 mx-auto" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            جاري بدء المقابلة
          </h2>
          <p className="text-gray-600">
            يرجى الانتظار بينما نقوم بالاتصال بغرفة المقابلة...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gray-100 flex flex-col"
      dir="rtl"
    >
      {/* Toast */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      {/* Hidden audio element for remote audio track */}
      <audio ref={audioRef} autoPlay playsInline />

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Video Area */}
        <div className="flex-1 relative">
          {/* AI Interviewer - Main View */}
          <div className="absolute inset-2 sm:inset-4 sm:inset-e-4 lg:inset-e-80 rounded-2xl sm:rounded-3xl overflow-hidden shadow-strong">
            <AIInterviewerVideo
              remoteVideoTrack={remoteVideoTrack}
            />

            {/* LiveKit connection status */}
            {isConnectingLiveKit && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="text-white text-center">
                  <Spinner size="lg" className="mb-4 mx-auto" />
                  <p>جاري الاتصال بالمقابلة...</p>
                </div>
              </div>
            )}
            {livekitError && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="text-white text-center bg-red-500/80 p-6 rounded-xl max-w-md">
                  <p className="font-bold mb-2">خطأ في الاتصال</p>
                  <p className="text-sm">{livekitError}</p>
                </div>
              </div>
            )}
          </div>

          {/* User Video - Picture in Picture */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="absolute bottom-20 sm:bottom-auto sm:top-8 start-2 sm:start-auto sm:end-8 w-28 sm:w-40 lg:w-56 aspect-video rounded-xl sm:rounded-2xl overflow-hidden shadow-strong border-2 border-white/20"
          >
            <VideoPreview
              stream={localStream}
              isActive={!!localStream}
              showStatus={false}
              mirrored={true}
              className="w-full h-full"
            />
            {/* You label */}
            <div className="absolute bottom-2 start-2">
              <span className="px-2 py-1 bg-black/50 backdrop-blur-sm rounded text-white text-xs">
                أنت
              </span>
            </div>
          </motion.div>

          {/* Recording indicator */}
          {isRecording && (
            <div className="absolute top-4 sm:top-8 end-2 sm:start-8 sm:end-auto flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-2 bg-red-500/90 backdrop-blur-sm rounded-full text-xs sm:text-sm">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              <span className="text-white text-sm font-medium">
                REC {formatElapsedTime()}
              </span>
            </div>
          )}

          {/* Network quality indicator - REMOVED as per user request */}
        </div>

        {/* Transcript Panel - Hidden on mobile */}
        <motion.div
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="hidden lg:flex w-80 bg-white border-s border-gray-200 flex-col"
        >
          {/* /<TranscriptPanel messages={transcript} /> */}
        </motion.div>
      </div>

      {/* Control Bar */}
      <ControlBar
        time={getCurrentTime()}
        interviewType={text.technicalInterview}
        micEnabled={state.microphoneEnabled}
        cameraEnabled={state.cameraEnabled}
        onToggleMic={handleToggleMic}
        onToggleCamera={handleToggleCamera}
        onEndInterview={handleEndInterview}
        onSettings={() => {}}
      />

      {/* End Interview Modal */}
      <Modal
        isOpen={showEndModal}
        onClose={() => setShowEndModal(false)}
        title={text.endTitle}
      >
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <EndCallIcon size={32} className="text-red-600" />
          </div>
          <p className="text-gray-600 mb-6">
            {text.endMessage}
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              variant="danger"
              onClick={confirmEndInterview}
            >
              {text.endYes}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowEndModal(false)}
            >
              {text.endNo}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Ending Overlay */}
      {isEnding && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
        >
          <div className="bg-white rounded-2xl p-8 text-center shadow-strong max-w-md w-full mx-4">
            <Spinner size="lg" className="mb-4 mx-auto" />
            <p className="text-gray-700 font-medium">{text.saving}</p>

            {/* Upload Progress */}
            {uploadProgress.stage === 'uploading' && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-primary-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress.progress}%` }}
                  />
                </div>
                <p className="text-gray-600 text-sm mt-2">
                  {uploadProgress.progress}%
                </p>
              </div>
            )}

            {uploadProgress.stage === 'finalizing' && (
              <p className="text-gray-500 text-sm mt-2">جاري إنهاء الرفع...</p>
            )}

            {uploadProgress.stage === 'waiting' && (
              <p className="text-gray-500 text-sm mt-2">جاري حفظ البيانات...</p>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default InterviewPage;
